import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegHeart, FaUserPlus, FaUserCheck, FaComment } from "react-icons/fa";

const UserSuggestionsList = ({
  users,
  loading,
  error,
  authUser,
  likeStates,
  friendRequestStates,
  matches,
  friendRequests,
  friends,
  filters,
  handleLike,
  handleUnlike,
  sendFriendRequest,
  cancelFriendRequest,
  handleOpenChat,
  isLoadingLike,
  isLoadingFriend,
}) => {
  // State chung cho tooltip thông báo
  const [tooltip, setTooltip] = useState(null); // { message, type }
  const [isTooltipFading, setIsTooltipFading] = useState(false);

  // Hàm hiển thị tooltip
  const showTooltip = (message, type = "success") => {
    setTooltip({ message, type });
    setTimeout(() => {
      setIsTooltipFading(true);
      setTimeout(() => {
        setTooltip(null);
        setIsTooltipFading(false);
      }, 300);
    }, 1500);
  };

  if (loading) {
    return <p className="text-center text-gray-500">Đang tải danh sách người dùng...</p>;
  }

  if (error) {
    return <p className="text-red-500 text-center mb-4">{error}</p>;
  }

  // Loại bỏ người dùng hiện tại và những người đã là bạn bè
  const filteredUsers = users.filter((user) => {
    if (!user || !user._id || !authUser || !authUser._id) return false;
    const isCurrentUser = String(user._id) === String(authUser._id);
    const isFriend = friends.some(
      (friend) => friend && friend._id && String(friend._id) === String(user._id)
    );
    return !isCurrentUser && !isFriend;
  });

  if (!filteredUsers.length) {
    return <p className="text-center text-gray-500">Không tìm thấy người dùng nào.</p>;
  }

  return (
    <>
      <div className="grid grid-cols-5 gap-6">
        {filteredUsers.map((user) => {
          const age = Math.floor(
            (new Date() - new Date(user.dateOfBirth)) / (1000 * 60 * 60 * 24 * 365)
          );
          const isLiked =
            authUser &&
            authUser._id &&
            (likeStates[user._id] ||
              matches.some((match) => String(match.likedUserId) === user._id));
          const isFriendRequestSent =
            authUser &&
            authUser._id &&
            (friendRequestStates[user._id] ||
              friendRequests.some(
                (req) =>
                  String(req.senderId) === String(authUser._id) &&
                  String(req.receiverId) === user._id &&
                  req.status === "pending"
              ));

          return (
            <div
              key={user._id}
              className="border rounded-lg p-0 shadow hover:shadow-md transition-shadow bg-white flex flex-col items-center"
            >
              {/* Click vào avatar để chuyển đến trang Profile */}
              <Link to={`/profile/${user.membershipId || user._id}`}>
                <div className="relative w-[auto] h-[240px]">
                  <img
                    src={user.primaryPhoto?.photoUrl || user.mainImage || user.avatar || "https://placehold.co/250x220"}
                    alt={user.name}
                    // Kích thước cố định, không có viền, chỉ bo góc trên
                    //object-contain hiển thị toàn bộ nội dung hình thu nhỏ /object-cover hiên thị cắt hình phù hợp
                    className="w-[260px] h-[240px] object-cover border-0 border-white rounded-t-lg rounded-b-none shadow-sm"
                    onError={(e) => (e.target.src = "https://placehold.co/180x240")}
                  />
                </div>
              </Link>
              <div className="text-center mt-2 px-4">
                <p className="font-semibold text-gray-800">
                  {user.name}, {age}
                </p>
                <p className="text-sm text-gray-600">
                  {user.locationText?.city}, {user.locationText?.country}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Tìm:{" "}
                  {user.gender === "male"
                    ? "Nam"
                    : user.gender === "female"
                    ? "Nữ"
                    : "Khác"}{" "}
                  {filters.minAge || "18"} - {filters.maxAge || "60"}
                </p>
                <p className="text-xs text-gray-500">
                  {user.isOnline ? "Tôi đang trên mạng" : "Cách đây vài phút"}
                </p>
              </div>
              <div className="mt-2 flex space-x-2 mb-4">
                {/* Nút Like / Unlike */}
                <button
                  onClick={async () => {
                    if (isLiked) {
                      try {
                        await handleUnlike(user._id);
                        showTooltip("Unlike successful!", "success");
                      } catch (err) {
                        showTooltip(err.message || "Error unliking", "error");
                      }
                    } else {
                      try {
                        await handleLike(user._id);
                        showTooltip("Like sent successfully!", "success");
                      } catch (err) {
                        showTooltip(err.message || "Error sending like", "error");
                      }
                    }
                  }}
                  className={`p-2 rounded ${
                    isLiked
                      ? "bg-gray-400 text-white hover:bg-gray-500"
                      : "bg-red-500 text-white hover:bg-red-600"
                  } ${isLoadingLike ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={isLoadingLike}
                  title={isLiked ? "Bỏ thích" : "Thích"}
                >
                  {isLiked ? <FaHeart /> : <FaRegHeart />}
                </button>
                {/* Nút Kết bạn */}
                <button
                  onClick={async () => {
                    if (isFriendRequestSent) {
                      try {
                        await cancelFriendRequest(user._id);
                        showTooltip("Friend request canceled!", "success");
                      } catch (err) {
                        showTooltip(err.message || "Error canceling friend request", "error");
                      }
                    } else {
                      try {
                        await sendFriendRequest(user._id);
                        showTooltip("Friend request sent!", "success");
                      } catch (err) {
                        showTooltip(err.message || "Error sending friend request", "error");
                      }
                    }
                  }}
                  className={`p-2 rounded ${
                    isFriendRequestSent
                      ? "bg-yellow-500 text-white hover:bg-yellow-600"
                      : "bg-green-500 text-white hover:bg-green-600"
                  } ${isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={isLoadingFriend}
                  title={isFriendRequestSent ? "Hủy yêu cầu kết bạn" : "Kết bạn"}
                >
                  {isFriendRequestSent ? <FaUserCheck /> : <FaUserPlus />}
                </button>
                {/* Nút Chat */}
                {handleOpenChat && (
                  <button
                    onClick={async () => {
                      try {
                        await handleOpenChat(user);
                        showTooltip("Chat opened successfully!", "success");
                      } catch (err) {
                        showTooltip(err.message || "Error opening chat", "error");
                      }
                    }}
                    className="p-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                    title="Chat"
                  >
                    <FaComment />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Tooltip chung hiển thị thông báo */}
      {tooltip && (
        <div
          className={`fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
            isTooltipFading ? "animate-fade-out" : "animate-slide-in"
          } ${tooltip.type === "success" ? "bg-green-500" : "bg-red-500"}`}
        >
          <span>{tooltip.message}</span>
          {/* <button
            onClick={() => {
              setIsTooltipFading(true);
              setTimeout(() => {
                setTooltip(null);
                setIsTooltipFading(false);
              }, 300);
            }}
            className="text-white underline hover:text-gray-200"
          >
            Bỏ qua
          </button> */}
        </div>
      )}
    </>
  );
};

export default UserSuggestionsList;